import React from "react";
import { Box, CircularProgress, Tooltip } from "@mui/material";
import { FiCheckSquare, FiSquare, FiSend, FiZap, FiStar, FiKey, FiGrid, FiClipboard, FiTool, FiNavigation } from "react-icons/fi";
import { FaShower } from "react-icons/fa";
import { ProductSaleItem, ProductTypes } from "../../../../logic/api";

interface ProductLozengeProps {
    saleItem: ProductSaleItem;
    isServiceable: boolean;
    isCompleted: boolean | null;
    isLoading: boolean;
    onClick?: () => void;
}

const PRODUCT_TYPE_CONFIG: Record<number, { icon?: React.ReactNode; color: string }> = {
    [ProductTypes.default]: { color: "#546e7a" },
    [ProductTypes.valet]: { icon: <FaShower size={14} />, color: "#1976d2" },
    [ProductTypes.collection]: { icon: <FiNavigation size={14} />, color: "#573022" },
    [ProductTypes.delivery]: { icon: <FiSend size={14} />, color: "#e91e63" },
    [ProductTypes.electricVehicle]: { icon: <FiZap size={14} />, color: "#2e7d32" },
    [ProductTypes.vip]: { icon: <FiStar size={14} />, color: "#6a1b9a" },
    [ProductTypes.selfPark]: { icon: <FiKey size={14} />, color: "#546e7a" },
    [ProductTypes.blockPark]: { icon: <FiGrid size={14} />, color: "#e65100" },
    [ProductTypes.mot]: { icon: <FiClipboard size={14} />, color: "#546e7a" },
    [ProductTypes.service]: { icon: <FiTool size={14} />, color: "#546e7a" },
};

const ProductLozenge: React.FC<ProductLozengeProps> = ({ saleItem, isServiceable, isCompleted, isLoading, onClick }) => {
    const config = PRODUCT_TYPE_CONFIG[saleItem.productType] ?? { color: "#546e7a" };
    const { color } = config;

    const renderCheckbox = () => {
        if (!isServiceable) return null;
        if (isLoading) return <CircularProgress size={14} />;
        if (isCompleted) return <FiCheckSquare size={16} color="green" />;
        return <FiSquare size={16} />;
    };

    return (
        <Tooltip title={saleItem.name}>
            <Box
                data-testid={`product-lozenge-${saleItem.id}`}
                onClick={onClick}
                tabIndex={onClick ? 0 : undefined}
                role={onClick ? "button" : undefined}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    border: `1.5px solid ${color}`,
                    color,
                    borderRadius: 2,
                    p: "6px 10px",
                    cursor: onClick ? "pointer" : "default",
                    whiteSpace: "nowrap",
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    fontWeight: "bold",
                    transition: "background-color 150ms, box-shadow 150ms",
                    minWidth: 0,
                    maxWidth: "200px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    ...(onClick && {
                        "&:hover": {
                            bgcolor: `${color}14`,
                        },
                        "&:focus-visible": {
                            outline: `2px solid ${color}`,
                            outlineOffset: 1,
                        },
                    }),
                }}
            >
                {config.icon}
                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {saleItem.name}
                </Box>
                {renderCheckbox()}
            </Box>
        </Tooltip>
    );
};

export default ProductLozenge;
